import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { User, Lock, Eye, EyeOff } from "lucide-react";
import {
  loginUser,
  verifyOTP,
  clearError,
} from "../../RTK/login/Authentication";

import { usePermissions } from "../../contexts/PermissionsContext"; 


const LoginForm = () => {
  // Redux hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { savePermissions } = usePermissions();

  // Get auth state from Redux
  const { isLoading, error, requires2FA, isAuthenticated } = useSelector(
    (state) => state.auth
  );

  // Local state
  const [formNo, setFormNo] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    otpValues: ["", "", "", "", "", ""],
  });

  // Clear errors on mount
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Handle authentication state changes
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  // Handle 2FA requirement
  useEffect(() => {
    if (requires2FA) {
      setFormNo(2);
      toast.info("تم إرسال رمز التحقق");
    }
  }, [requires2FA]);

  // Handle error messages
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Form input handlers
  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleOTPChange = (index, value) => {
    if (value.length <= 1) {
      const newOTPValues = [...formData.otpValues];
      newOTPValues[index] = value;
      setFormData((prev) => ({ ...prev, otpValues: newOTPValues }));

      if (value && index < 5) {
        document.getElementById(`otp-${index + 1}`)?.focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !formData.otpValues[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  // Form submission handlers
  const handleLogin = async () => {
    const { username, password } = formData;

    if (!username || !password) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    try {
      const resultAction = await dispatch(loginUser({ username, password }));

      if (loginUser.fulfilled.match(resultAction)) {
        const { requires_2fa, user, permissions } = resultAction.payload.data;

        // ✨ احفظ الصلاحيات والـ role
        if (user && permissions) {
          savePermissions(permissions, user.role);
          console.log("✅ Permissions saved:", permissions);
          console.log("✅ User role:", user.role);
        }

        if (!requires_2fa) {
          toast.success("تم تسجيل الدخول بنجاح");
        }
      }
    } catch (err) {
      console.error("Login error:", err);
    }
  };

  const handleOTPVerify = async () => {
    const otp_code = formData.otpValues.join("");
    const { username } = formData;

    if (otp_code.length !== 6) {
      toast.error("يرجى إدخال رمز التحقق كاملاً");
      return;
    }

    try {
      const resultAction = await dispatch(verifyOTP({ otp_code, username }));
      
      if (verifyOTP.fulfilled.match(resultAction)) {
        const { user, permissions } = resultAction.payload.data;

        // ✨ حدّث الصلاحيات بعد 2FA
        if (user && permissions) {
          savePermissions(permissions, user.role);
          console.log("✅ Permissions updated after 2FA:", permissions);
        }

        toast.success("تم التحقق بنجاح");
      }
    } catch (err) {
      toast.error("فشل التحقق من الرمز");
    }
  };

  // Render methods
  const renderLoginStep = () => (
    <div className="space-y-5">
      {/* Username field */}
      <div className="flex flex-col">
        <label
          htmlFor="username"
          className="text-sm font-medium text-gray-300 mb-1"
        >
          اسم المستخدم
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <User className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="username"
            name="username"
            type="text"
            value={formData.username}
            onChange={handleInputChange}
            className="w-full pr-10 p-3 bg-gray-700 border border-gray-600 text-white 
              rounded-lg outline-none focus:border-blue-500 transition-all rtl"
            placeholder="أدخل اسم المستخدم"
          />
        </div>
      </div>

      {/* Password field */}
      <div className="flex flex-col">
        <label
          htmlFor="password"
          className="text-sm font-medium text-gray-300 mb-1"
        >
          كلمة المرور
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <Lock className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleInputChange}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleLogin();
              }
            }}
            className="w-full pr-10 pl-10 p-3 bg-gray-700 border border-gray-600 text-white 
              rounded-lg outline-none focus:border-blue-500 transition-all rtl"
            placeholder="أدخل كلمة المرور"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 
              hover:text-gray-300 transition-colors"
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5" />
            ) : (
              <Eye className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      {/* Login button */}
      <button
        onClick={handleLogin}
        disabled={isLoading}
        className="w-full py-3 px-4 text-white bg-blue-600 hover:bg-blue-700 
          rounded-lg transition-colors duration-200 font-medium mt-6 disabled:opacity-50"
      >
        {isLoading ? "جاري التحميل..." : "تسجيل الدخول"}
      </button>
    </div>
  );

  const renderOTPStep = () => (
    <div className="space-y-6">
      <div className="flex justify-between space-x-2 flex-row-reverse">
        {formData.otpValues.map((digit, index) => (
          <input
            key={index}
            id={`otp-${index}`}
            type="text"
            maxLength={1}
            value={digit}
            onChange={(e) => handleOTPChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className="w-12 h-12 text-center text-xl bg-gray-700 border border-gray-600 
              text-white rounded-lg outline-none focus:border-blue-500 focus:ring-1 
              focus:ring-blue-500 transition-all"
          />
        ))}
      </div>

      <div className="mt-6 gap-3 flex justify-center items-center">
        <button
          onClick={() => setFormNo(1)}
          className="w-full py-3 px-4 text-white bg-gray-700 hover:bg-gray-600 
            rounded-lg transition-colors duration-200 font-medium"
        >
          رجوع
        </button>
        <button
          onClick={handleOTPVerify}
          disabled={isLoading}
          className="w-full py-3 px-4 text-white bg-blue-600 hover:bg-blue-700 
            rounded-lg transition-colors duration-200 font-medium disabled:opacity-50"
        >
          {isLoading ? "جاري التحقق..." : "تحقق"}
        </button>
      </div>

      <div className="text-center">
        <button className="text-sm text-blue-400 hover:text-blue-300">
          لم تستلم الرمز؟ إعادة إرسال
        </button>
      </div>
    </div>
  );

  // Main render
  return (
    <div
      className="w-screen h-screen bg-gray-900 flex justify-center items-center"
      dir="rtl"
    >
      <ToastContainer theme="dark" position="top-left" />
      <div className="card w-[370px] md:w-[420px] rounded-xl shadow-2xl bg-gray-800 p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold leading-10 text-white text-center">
            {formNo === 1 ? "مرحباً بعودتك" : "التحقق من الرمز"}
          </h1>
          <p className="text-gray-400 text-center mt-2">
            {formNo === 1
              ? "تسجيل الدخول إلى حسابك"
              : "أدخل الرمز المرسل إلى جهازك"}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between items-center mb-8">
          {[1, 2].map((step, i) => (
            <React.Fragment key={i}>
              <div
                className={`w-[35px] my-3 text-white rounded-full 
                ${formNo >= step ? "bg-blue-600" : "bg-gray-600"} 
                h-[35px] flex justify-center items-center`}
              >
                {step}
              </div>
              {i === 0 && (
                <div
                  className={`w-full h-[3px] 
                  ${formNo === 2 ? "bg-blue-600" : "bg-gray-600"}`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Form Steps */}
        {formNo === 1 ? renderLoginStep() : renderOTPStep()}
      </div>
    </div>
  );
};

export default LoginForm;
